import {FC, ComponentProps, useCallback, useMemo, useContext} from 'react';
import {Button} from 'semantic-ui-react';
import {Link} from 'react-router-dom';
import {filter, map} from 'lodash';
import {DurationInput, ValueRenderer, interpolateRoute, request} from 'apstra-ui-common';

import generateProbeURI from '../generateProbeURI';
import {interfacesRenderer} from './InterfacesInput';
import {environmentExpectationsRenderer} from './EnvironmentExpectationsInput';
import {queueSpecificThresholdRenderer} from './QueueSpecificThresholdInput';
import {systemHealthThresholdsRenderer} from './SystemHealthThresholdInput';
import PredefinedEntityModal from './PredefinedEntityModal';
import IBAContext from '../IBAContext';
import TagsInput from '../../components/TagsInput';
import {Probe} from '../types';

const durationRenderer = new ValueRenderer({
  condition: ({name = '', schema}) =>
    /(?:^|\w_)(?:period|duration|time_window|interval)$/.test(name) &&
    (schema?.type === 'number' || schema?.type === 'integer'),
  ValueInput: ({name, value, schema, required, disabled, errors, onChange, fieldProps}) =>
    <DurationInput
      name={name}
      value={value}
      schema={schema}
      required={required}
      disabled={disabled}
      errors={errors}
      customValueType='duration'
      textPrefix={null}
      onChange={onChange}
      fieldProps={fieldProps}
    />
});

const tagsRenderer = new ValueRenderer({
  condition: ({name = '', schema}) =>
    /(?:^|\w_)tags$/.test(name) && schema?.type === 'array' && (schema.items as any)?.type === 'string',
  ValueInput: ({name, value, schema, required, disabled, errors, onChange, fieldProps}) => {
    const {blueprintTags} = useContext(IBAContext);
    const knownTags = map(blueprintTags, ({label}) => label);
    return (
      <TagsInput
        name={name}
        value={value}
        schema={schema}
        required={required}
        disabled={disabled}
        errors={errors}
        onChange={onChange}
        knownTags={knownTags}
        fieldProps={fieldProps}
      />
    );
  }
});

const generateResourceHref = ({blueprintId, entityId}) => generateProbeURI({blueprintId, probeId: entityId});

export type PredefinedProbeModalProps = {
  probe?: Probe;
  processorName?: string;
  stageName?: string;
  defaultPredefinedProbeName?: string;
} & Omit<
  ComponentProps<typeof PredefinedEntityModal>,
  'extraActions' | 'baseRoute' | 'generateResourceHref' | 'entities' | 'parameters' | 'setEntityName' | 'setParameter'
>;

const PredefinedProbeModal: FC<PredefinedProbeModalProps> = ({
  mode = 'create', probe, processorName, stageName, defaultPredefinedProbeName, ...props
}) => {
  const {predefinedProbes, blueprintId, routes} = useContext(IBAContext);
  const entities = useMemo(
    () => mode === 'create' ? filter(predefinedProbes, (probe) => !probe.experimental) : predefinedProbes,
    [mode, predefinedProbes]
  );
  const fetchPredefinedProbeParameters = useCallback(
    async ({routes, blueprintId, entityName, entityId, signal}) => {
      const route = routes.predefinedProbeParameters;
      const result = await request(
        interpolateRoute(route, {blueprintId, predefinedProbe: entityName, probeId: entityId}),
        {signal}
      );
      return mode === 'clone' ? {...result, label: `${result.label} copy`} : result;
    },
    [mode]
  );

  return (
    <PredefinedEntityModal
      {...props}
      mode={mode}
      resourceName='Predefined Probe'
      entities={entities}
      entityName={
        defaultPredefinedProbeName ? defaultPredefinedProbeName :
        probe ? probe.predefined_probe :
        undefined
      }
      entityId={probe?.id}
      fetchEntityParameters={probe ? fetchPredefinedProbeParameters : undefined}
      baseRoute={routes.predefinedProbeList}
      generateResourceHref={generateResourceHref}
      renderers={[
        interfacesRenderer,
        durationRenderer,
        tagsRenderer,
        environmentExpectationsRenderer,
        queueSpecificThresholdRenderer,
        systemHealthThresholdsRenderer,
      ]}
      extraActions={
        mode !== 'create' &&
          <Button
            as={Link}
            to={generateProbeURI({blueprintId, probeId: probe?.id, action: mode, processorName, stageName})}
            secondary
            icon='edit'
            size='large'
            content='Edit Processors'
          />
      }
    />
  );
};

export default PredefinedProbeModal;
